import { Check, ChevronsUpDown } from "lucide-react"
import {
	type ChangeEvent,
	Children,
	type ComponentProps,
	Fragment,
	isValidElement,
	type ReactNode,
	useState,
} from "react"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/shared/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover"
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from "@/shared/ui/select"

type SelectOption = { value: string; label: ReactNode; disabled?: boolean }
type SelectOptionGroup = { label: string; options: SelectOption[] }

/** Radix Select не принимает пустое значение у пункта: пустое кодируем отдельно. */
const EMPTY_SELECT_VALUE = "__empty__"

function collectOptions(children: ReactNode): (SelectOption | SelectOptionGroup)[] {
	const result: (SelectOption | SelectOptionGroup)[] = []
	Children.forEach(children, (child) => {
		if (!isValidElement(child)) return
		const props = child.props as {
			children?: ReactNode
			value?: unknown
			label?: unknown
			disabled?: boolean
		}
		if (child.type === Fragment) {
			result.push(...collectOptions(props.children))
		} else if (child.type === "option") {
			result.push({
				value: String(props.value ?? ""),
				label: props.children,
				disabled: props.disabled,
			})
		} else if (child.type === "optgroup") {
			result.push({
				label: String(props.label ?? ""),
				options: collectOptions(props.children).filter(
					(item): item is SelectOption => !("options" in item),
				),
			})
		}
	})
	return result
}

/** Длиннее: список с поиском: Radix Select рисует все пункты сразу и на сотнях групп подтормаживает. */
const SEARCH_THRESHOLD = 10

function textOf(node: ReactNode): string {
	if (node === null || node === undefined || typeof node === "boolean") return ""
	if (typeof node === "string" || typeof node === "number") return String(node)
	if (Array.isArray(node)) return node.map(textOf).join("")
	if (isValidElement(node)) return textOf((node.props as { children?: ReactNode }).children)
	return ""
}

function changeEvent(value: string) {
	return {
		target: { value },
		currentTarget: { value },
	} as unknown as ChangeEvent<HTMLSelectElement>
}

/** Выбор из длинного списка: поле поиска и пункты, отфильтрованные по тексту. */
function SearchSelect({
	items,
	value,
	onChange,
	disabled,
	id,
	className,
	ariaLabel,
	ariaLabelledBy,
}: {
	items: (SelectOption | SelectOptionGroup)[]
	value: string
	onChange?: (event: ChangeEvent<HTMLSelectElement>) => void
	disabled?: boolean
	id?: string
	className?: string
	ariaLabel?: string
	ariaLabelledBy?: string
}) {
	const [open, setOpen] = useState(false)
	const flat = items.flatMap((item) => ("options" in item ? item.options : [item]))
	const selected = flat.find((option) => option.value === value)
	const renderItem = (option: SelectOption) => (
		<CommandItem
			key={option.value || EMPTY_SELECT_VALUE}
			value={option.value || EMPTY_SELECT_VALUE}
			keywords={[textOf(option.label)]}
			disabled={option.disabled}
			onSelect={() => {
				setOpen(false)
				if (option.value !== value) onChange?.(changeEvent(option.value))
			}}
		>
			<Check className={cn("h-4 w-4", option.value === value ? "opacity-100" : "opacity-0")} />
			<span className="truncate">{option.label}</span>
		</CommandItem>
	)
	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					id={id}
					type="button"
					variant="outline"
					role="combobox"
					aria-expanded={open}
					aria-label={ariaLabel}
					aria-labelledby={ariaLabelledBy}
					disabled={disabled}
					className={cn("h-12 w-full justify-between px-3 font-normal", className)}
				>
					<span className={cn("truncate", !selected && "text-muted-foreground")}>
						{selected ? selected.label : "Выберите"}
					</span>
					<ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			{/* Внутри Sheet и Dialog колесо мыши блокируется, пока событие не остановлено здесь. */}
			<PopoverContent
				align="start"
				className="w-(--radix-popover-trigger-width) min-w-64 p-0"
				onWheel={(event) => event.stopPropagation()}
				onTouchMove={(event) => event.stopPropagation()}
			>
				<Command>
					<CommandInput placeholder="Поиск…" />
					<CommandList className="max-h-72">
						<CommandEmpty>Ничего не нашлось</CommandEmpty>
						{items.map((item, index) =>
							"options" in item ? (
								<CommandGroup key={`${item.label}-${index}`} heading={item.label}>
									{item.options.map(renderItem)}
								</CommandGroup>
							) : null,
						)}
						<CommandGroup>
							{items.filter((item): item is SelectOption => !("options" in item)).map(renderItem)}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}

/**
 * Выпадающий список shadcn с API нативного select: те же <option> и <optgroup> внутри
 * и onChange с event.target.value: вызывающий код не меняется. Длинный список: с поиском.
 */
export function NativeSelect({
	className,
	value,
	onChange,
	children,
	disabled,
	id,
	name,
	"aria-label": ariaLabel,
	"aria-labelledby": ariaLabelledBy,
}: ComponentProps<"select">) {
	const items = collectOptions(children)
	const optionCount = items.reduce(
		(count, item) => count + ("options" in item ? item.options.length : 1),
		0,
	)
	if (optionCount > SEARCH_THRESHOLD) {
		return (
			<SearchSelect
				items={items}
				value={value === undefined ? "" : String(value)}
				onChange={onChange}
				disabled={disabled}
				id={id}
				className={className}
				ariaLabel={ariaLabel}
				ariaLabelledBy={ariaLabelledBy}
			/>
		)
	}
	const toItemValue = (raw: string) => (raw === "" ? EMPTY_SELECT_VALUE : raw)
	const renderOption = (option: SelectOption) => (
		<SelectItem
			key={option.value || EMPTY_SELECT_VALUE}
			value={toItemValue(option.value)}
			disabled={option.disabled}
		>
			{option.label}
		</SelectItem>
	)
	return (
		<Select
			value={value === undefined ? undefined : toItemValue(String(value))}
			disabled={disabled}
			name={name}
			onValueChange={(next) => {
				const nextValue = next === EMPTY_SELECT_VALUE ? "" : next
				onChange?.({
					target: { value: nextValue },
					currentTarget: { value: nextValue },
				} as unknown as ChangeEvent<HTMLSelectElement>)
			}}
		>
			<SelectTrigger
				id={id}
				aria-label={ariaLabel}
				aria-labelledby={ariaLabelledBy}
				className={cn("h-12 w-full", className)}
			>
				<SelectValue />
			</SelectTrigger>
			<SelectContent className="max-h-80">
				{items.map((item, index) =>
					"options" in item ? (
						<SelectGroup key={`${item.label}-${index}`}>
							<SelectLabel>{item.label}</SelectLabel>
							{item.options.map(renderOption)}
						</SelectGroup>
					) : (
						renderOption(item)
					),
				)}
			</SelectContent>
		</Select>
	)
}
